import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { type Season, type WithActiveSeasonData } from "./season.types";
import {
  isAfterSeasonSubmissionsStart,
  isBeforeSeasonSubmissionsEnd,
  isSeasonAcceptingSubmissions,
} from "./season.utils";

export const useSeasonStore = defineStore("season", () => {
  const activeSeasons = ref<(Season & WithActiveSeasonData)[]>([]);
  const currentSeason = ref<(Season & WithActiveSeasonData) | undefined>();

  const currentSeasonId = computed(() => currentSeason.value?.id);

  const beforeSubmissionsEnd = computed<boolean>(() =>
    currentSeason.value ? isBeforeSeasonSubmissionsEnd(currentSeason.value) : false,
  );
  const afterSubmissionsStart = computed<boolean>(() =>
    currentSeason.value ? isAfterSeasonSubmissionsStart(currentSeason.value) : false,
  );
  const withinSubmissionDates = computed<boolean>(() =>
    currentSeason.value ? isSeasonAcceptingSubmissions(currentSeason.value) : false,
  );

  return {
    /**
     * Active season(s) (may be multiple in rare cases)
     *
     * NOTE: Use `currentSeason` (user selected) for any operations/actions requiring a specific season.
     */
    activeSeasons,
    /** Whether current season is accepting submissions (ie. is today within start/end of current season) */
    withinSubmissionDates,
    /**
     * Whether today is before submissions end (of current season).
     *
     * NOTE: Does not calculate whether submissions have started!
     */
    beforeSubmissionsEnd,
    /**
     * Whether today is after submissions start (of current season).
     *
     * NOTE: Does not calculate whether submissions have ended!
     */
    afterSubmissionsStart,
    /**
     * Selected season
     *
     * NOTE: Intended to be used with active seasons; however, collaborators may choose to view other
     *         (non-active) seasons for support/historical purposes. When a non-active season is selected,
     *         some active-specific data may not be available (ie. `submissionsCapped`, etc)!
     */
    currentSeason,
    /** Selected season ID */
    currentSeasonId,
    /**
     * Whether overall season submission limit has been reached
     *
     * NOTE: Will not be set for selected seasons that are not currently active!
     */
    submissionsCapped: computed(() => currentSeason.value?.submissionsCapped ?? false),
  };
});
